/**
 * commitmentPrefill — the router-state contract that pre-fills the
 * Commitment Registry's inline create form from a Decision (break 1 of the
 * decision → commitment → term/condition → signal → verdict → reprioritization
 * chain audit).
 *
 * The `commitments` table (migration behind src/types/prioritization/commitment.ts)
 * carries no `decision_id` column — there is no schema edge from a Commitment
 * back to the Decision that produced it. Until that column exists, provenance
 * rides the one free-text field the schema does have: `description`. This
 * module builds that description deterministically so the same decision
 * always produces the same provenance sentence, and is the one place that
 * sentence's shape is defined.
 *
 * Contract mirrors sessionPrefill.ts: an entry point that already has an
 * object on screen navigates with router state —
 *
 *   navigate('/prioritization/commitments', { state: { commitmentPrefill } })
 *
 * — and the registry page validates defensively (parseCommitmentPrefill),
 * so a stale or garbled state object degrades to "nothing pre-filled" rather
 * than throwing. A refresh drops router state, which is the correct
 * degradation here too.
 */

const MAX_TITLE = 200;

export interface CommitmentPrefill {
  /** Seeds the commitment title field. */
  title: string;
  /** Provenance sentence, written into the description field verbatim. */
  description: string;
  /** The decision this commitment traces back to (not a schema edge — see module doc). */
  decisionId: string;
  decisionTitle: string;
  /** When the decision names an item, the commitment links it on creation. */
  itemId?: string | null;
}

export interface DecisionForCommitmentPrefill {
  id: string;
  title: string;
  itemId?: string | null;
}

/** The provenance sentence written into the commitment's description field. */
export function decisionProvenanceNote(decisionId: string, decisionTitle: string): string {
  return `Created from decision "${decisionTitle}" (decisions/${decisionId}).`;
}

export function buildCommitmentPrefillFromDecision(
  decision: DecisionForCommitmentPrefill
): CommitmentPrefill {
  const title = decision.title.trim().slice(0, MAX_TITLE) || 'Untitled commitment';
  return {
    title,
    description: decisionProvenanceNote(decision.id, decision.title),
    decisionId: decision.id,
    decisionTitle: decision.title,
    itemId: decision.itemId ?? null,
  };
}

/**
 * Validate untyped router state into a CommitmentPrefill, or null when there
 * is nothing usable. Tolerates junk field-by-field, same posture as
 * parseSessionPrefill.
 */
export function parseCommitmentPrefill(state: unknown): CommitmentPrefill | null {
  if (!state || typeof state !== 'object') return null;
  const raw = (state as { commitmentPrefill?: unknown }).commitmentPrefill;
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;

  if (typeof p.decisionId !== 'string' || !p.decisionId) return null;
  if (typeof p.decisionTitle !== 'string' || !p.decisionTitle.trim()) return null;

  const title =
    typeof p.title === 'string' && p.title.trim()
      ? p.title.trim().slice(0, MAX_TITLE)
      : p.decisionTitle.trim().slice(0, MAX_TITLE);

  const description =
    typeof p.description === 'string' && p.description.trim()
      ? p.description
      : decisionProvenanceNote(p.decisionId, p.decisionTitle);

  const itemId = typeof p.itemId === 'string' && p.itemId ? p.itemId : null;

  return {
    title,
    description,
    decisionId: p.decisionId,
    decisionTitle: p.decisionTitle.trim(),
    itemId,
  };
}
